import { useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  listConversations,
  getMessages,
  renameConversation,
  deleteConversation,
} from '../services/conversations-api';
import { nextPage } from '../services/assistant-client';
import { useAssistantScope } from './use-assistant-scope';

const KEY = ['assistant', 'conversations'];

export function useConversations(search = '') {
  const scope = useAssistantScope();
  const q = useInfiniteQuery({
    queryKey: [...KEY, scope, search],
    queryFn: ({ pageParam }) => listConversations(pageParam, search),
    initialPageParam: 1,
    getNextPageParam: nextPage,
  });
  const conversations = useMemo(
    () => q.data?.pages.flatMap((p) => p.results),
    [q.data]
  );
  return {
    conversations,
    isLoading: q.isLoading,
    error: q.error,
    loadMore: q.fetchNextPage,
    hasMore: q.hasNextPage,
    loadingMore: q.isFetchingNextPage,
  };
}

/**
 * A transcript, paged backwards from the latest message (10 per page) and
 * flattened OLDEST-FIRST for rendering. `loadOlder` walks further back.
 */
export function useConversationMessages(id: number | null) {
  const scope = useAssistantScope();
  const q = useInfiniteQuery({
    queryKey: ['assistant', 'messages', id, scope],
    // A mounted thread already receives new messages over its stream. A focus
    // refetch would pull those same live turns into the history prefix twice.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: ({ pageParam }) => getMessages(id!, pageParam),
    enabled: !!id,
    initialPageParam: 1,
    getNextPageParam: nextPage,
  });
  // Every page is newest-first and the pages walk backwards, so the whole
  // concatenation is one descending run — a single reverse puts it in order.
  const messages = useMemo(() => {
    const seen = new Set<string>();
    return (q.data?.pages ?? [])
      .flatMap((p) => p.results)
      .filter((message) => {
        if (!message.id) return true;
        if (seen.has(message.id)) return false;
        seen.add(message.id);
        return true;
      })
      .reverse();
  }, [q.data]);
  return {
    messages,
    isLoading: q.isLoading || (q.isFetching && !q.isFetchingNextPage),
    loadOlder: q.fetchNextPage,
    hasOlder: q.hasNextPage,
    loadingOlder: q.isFetchingNextPage,
  };
}

export function useRenameConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) =>
      renameConversation(id, title),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteConversation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
